import { useState } from "react";
import { addMonths, endOfMonth, format, parseISO, startOfMonth } from "date-fns";
import { tr } from "date-fns/locale";
import { CalendarRange, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { checkDate, computeRepeatDates, newSeriesId, WEEKDAYS } from "@/components/RecurringOptions";

const TYPE_MAP = { "Özel Ders": "one_on_one", "Düet": "duet", "Grup Dersi": "group", "Online": "one_on_one" };
const dayLabels = (days) => WEEKDAYS.filter((w) => days.includes(w.d)).map((w) => w.l).join("-");

const pickPackage = (pkgs, branch, from) => (pkgs || [])
  .filter((p) => p.status === "active" && (p.remaining_sessions ?? 0) > 0 && (!p.end_date || p.end_date >= from)
    && (!branch || (p.package_branches?.length ? p.package_branches.includes(branch) : p.package_branch === branch)))
  .sort((a, b) => (a.end_date || "9999").localeCompare(b.end_date || "9999"))[0];

// Build one member's plan: package, dates and availability of each date
const planFor = async (m, from, to, businessId) => {
  const tpl = m.recurring_template;
  const row = { member: m, tpl, dates: [], warning: "" };
  if (!tpl.trainer_id || !tpl.room_id) return { ...row, warning: "Kayıtlı düzende antrenör veya oda yok" };
  const pkgs = await fetch(`${API}/members/${m.member_id}/packages`, { credentials: "include" }).then((r) => (r.ok ? r.json() : [])).catch(() => []);
  const pkg = pickPackage(pkgs, tpl.branch, from);
  if (!pkg) return { ...row, warning: `${tpl.branch || ""} için aktif ve hakkı olan paket yok` };
  const today = format(new Date(), "yyyy-MM-dd");
  const all = computeRepeatDates({ start: parseISO(from), enabled: true, type: "weekdays", days: tpl.days, endMode: "date", until: to }).filter((d) => d >= today);
  const trainingType = TYPE_MAP[pkg.session_type] || "one_on_one";
  const branch = tpl.branch || pkg.package_branch;
  const checks = await Promise.all(all.map((date) => checkDate({ date, startTime: tpl.start_time, endTime: tpl.end_time, trainerId: tpl.trainer_id, roomId: tpl.room_id, branch, trainingType, memberId: m.member_id, businessId })));
  let left = pkg.remaining_sessions;
  const dates = all.map((date, i) => {
    const reasons = checks[i].length ? checks[i] : left > 0 ? [] : ["Pakette hak kalmadı"];
    if (!reasons.length) left -= 1;
    return { date, reasons };
  });
  const skipped = dates.filter((d) => d.reasons.length).length;
  return { ...row, pkg, branch, trainingType, dates, warning: skipped ? `${skipped} tarih atlanacak` : "" };
};

const PlanRow = ({ row, on, toggle }) => {
  const good = row.dates.filter((d) => !d.reasons.length);
  return (
    <div className={`rounded-xl border p-2.5 text-xs space-y-1 ${on ? "bg-white" : "bg-slate-50 opacity-70"}`} data-testid={`bulk-row-${row.member.member_id}`}>
      <label className="flex items-center gap-2">
        <Checkbox checked={on} disabled={!good.length} onCheckedChange={toggle} />
        <span className="font-semibold text-sm">{row.member.name} {row.member.surname}</span>
        <span className="text-purple-700">{dayLabels(row.tpl.days)} {row.tpl.start_time}–{row.tpl.end_time}</span>
        <span className="ml-auto font-semibold">{good.length} ders</span>
      </label>
      {row.pkg && <p className="text-grey">Paket: {row.pkg.package_name} · kalan hak {row.pkg.remaining_sessions}</p>}
      {row.warning && <p className="text-amber-700 font-medium">{row.warning}</p>}
      <div className="flex flex-wrap gap-1">
        {row.dates.map((d) => (
          <span key={d.date} title={d.reasons[0] || ""} className={`px-1.5 py-0.5 rounded ${d.reasons.length ? "bg-red-100 text-red-700 line-through" : "bg-purple-50 text-purple-900"}`}>
            {format(parseISO(d.date), "d MMM EEE", { locale: tr })}
          </span>
        ))}
      </div>
    </div>
  );
};

export const BulkPlanDialog = ({ businessId, members, statusFor, onClose, onDone }) => {
  const next = addMonths(new Date(), 1);
  const [from, setFrom] = useState(format(startOfMonth(next), "yyyy-MM-dd"));
  const [to, setTo] = useState(format(endOfMonth(next), "yyyy-MM-dd"));
  const [rows, setRows] = useState(null);
  const [on, setOn] = useState({});
  const [busy, setBusy] = useState(false);
  const candidates = members.filter((m) => m.status !== "inactive" && m.recurring_template?.days?.length);

  const prepare = async () => {
    setBusy(true);
    const out = [];
    for (const m of candidates) out.push(await planFor(m, from, to, businessId));
    setRows(out);
    setOn(Object.fromEntries(out.map((r) => [r.member.member_id, r.dates.some((d) => !d.reasons.length)])));
    setBusy(false);
  };

  const create = async () => {
    setBusy(true);
    let ok = 0;
    const failed = [];
    for (const r of rows.filter((x) => on[x.member.member_id])) {
      const seriesId = newSeriesId();
      for (const d of r.dates.filter((x) => !x.reasons.length)) {
        const body = {
          business_id: businessId, member_id: r.member.member_id, trainer_id: r.tpl.trainer_id, room_id: r.tpl.room_id, branch: r.branch,
          date: d.date, start_time: r.tpl.start_time, end_time: r.tpl.end_time, training_type: r.trainingType, allow_shared: true,
          status: statusFor(r.member), is_recurring: true, add_to_online: true, payment_type: "package",
          member_package_id: r.pkg.member_package_id, package_id: r.pkg.member_package_id, is_demo: false, series_id: seriesId,
        };
        const res = await fetch(`${API}/reservations/create`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body) });
        if (res.ok) ok++;
        else failed.push(`${r.member.name} ${format(parseISO(d.date), "d MMM", { locale: tr })}: ${(await res.json().catch(() => ({}))).detail || "hata"}`);
      }
    }
    setBusy(false);
    if (ok) toast.success(`${ok} rezervasyon oluşturuldu`);
    if (failed.length) toast.error(`${failed.length} ders oluşturulamadı — ${failed.slice(0, 3).join(" · ")}`, { duration: 9000 });
    if (ok) onDone();
  };

  const total = (rows || []).filter((r) => on[r.member.member_id]).reduce((s, r) => s + r.dates.filter((d) => !d.reasons.length).length, 0);
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-2xl md:max-h-[90vh] overflow-y-auto content-start" data-testid="bulk-plan-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-navy"><CalendarRange className="w-5 h-5" /> Toplu Tekrar Planı</DialogTitle></DialogHeader>
        <p className="text-xs text-grey">Kayıtlı düzeni olan {candidates.length} üye için seçtiğiniz tarih aralığında rezervasyon oluşturulur. Dersler Online Ders Programı'na da eklenir.</p>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Başlangıç</Label><DatePickerTR value={from} onChange={(v) => { setFrom(v); setRows(null); }} returnDateString className="rounded-lg w-full mt-1" data-testid="bulk-from" /></div>
          <div><Label className="text-xs">Bitiş</Label><DatePickerTR value={to} onChange={(v) => { setTo(v); setRows(null); }} returnDateString minDate={parseISO(from)} className="rounded-lg w-full mt-1" data-testid="bulk-to" /></div>
        </div>
        {rows === null ? (
          <Button variant="outline" className="rounded-xl" disabled={busy || !candidates.length || !from || !to || to < from} onClick={prepare} data-testid="bulk-prepare-btn">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Müsaitlik kontrol ediliyor…</> : candidates.length ? "Planı Hazırla ve Kontrol Et" : "Kayıtlı düzeni olan üye yok"}
          </Button>
        ) : (
          <div className="space-y-2" data-testid="bulk-rows">
            {rows.map((r) => <PlanRow key={r.member.member_id} row={r} on={!!on[r.member.member_id]} toggle={() => setOn((s) => ({ ...s, [r.member.member_id]: !s[r.member.member_id] }))} />)}
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1 rounded-xl" disabled={busy} onClick={onClose} data-testid="bulk-cancel-btn">Vazgeç</Button>
          <Button className="flex-1 rounded-xl bg-navy hover:bg-navy/90" disabled={busy || !rows || !total} onClick={create} data-testid="bulk-create-btn">
            {busy && rows ? <Loader2 className="w-4 h-4 animate-spin" /> : `${total} rezervasyon oluştur`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
